import { useEffect, useMemo, useState } from 'react';

import type { KnowledgeParticle } from '../../types/domain';
import { useParticleEmbeddedImage } from './useParticleEmbeddedImage';
import {
  fetchWorkbenchParticle,
  getWorkbenchParticleCache,
  refreshWorkbenchParticle,
  setWorkbenchParticleCache,
} from './ttqWorkbenchParticleCache';

function readQuestionText(questions: unknown): string {
  if (typeof questions === 'string') return questions;
  if (questions && typeof questions === 'object' && 'main' in questions) {
    return String((questions as { main?: string }).main || '');
  }
  return '';
}

type UseTtqWorkbenchParticleOptions = {
  /** 为 false 时不发起请求；已缓存的颗粒仍可直接读取 */
  enabled?: boolean;
};

/** 仅加载单颗知识颗粒（不含 session），供 Group 多颗并行使用；进程内缓存，展开时加载一次 */
export function useTtqWorkbenchParticle(
  particleId: string,
  options: UseTtqWorkbenchParticleOptions = {},
) {
  const { enabled = true } = options;
  const cached = particleId ? getWorkbenchParticleCache(particleId) : null;
  const [particle, setParticle] = useState<KnowledgeParticle | null>(cached);
  const [loading, setLoading] = useState(Boolean(enabled && particleId && !cached));
  const [error, setError] = useState<string | null>(null);

  const { data: sourceImage, loading: imageLoading } = useParticleEmbeddedImage(
    particleId,
    Boolean(enabled && particleId),
  );

  useEffect(() => {
    if (!particleId) {
      setParticle(null);
      setLoading(false);
      return;
    }

    const hit = getWorkbenchParticleCache(particleId);
    if (hit) {
      setParticle(hit);
      setLoading(false);
      setError(null);
      return;
    }

    if (!enabled) {
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    setError(null);
    void fetchWorkbenchParticle(particleId)
      .then((p) => active && setParticle(p))
      .catch((e) => active && setError(e instanceof Error ? e.message : '加载颗粒失败'))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [enabled, particleId]);

  const questionText = useMemo(
    () => readQuestionText(particle?.questions),
    [particle?.questions],
  );
  const answerText = useMemo(
    () => readQuestionText(particle?.answers),
    [particle?.answers],
  );

  return {
    particle,
    loading,
    error,
    sourceImage,
    imageLoading,
    questionText,
    answerText,
    replaceParticle: (next: KnowledgeParticle) => {
      setWorkbenchParticleCache(next);
      setParticle(next);
    },
    reload: async () => {
      if (!particleId) return null;
      const next = await refreshWorkbenchParticle(particleId);
      setParticle(next);
      return next;
    },
  };
}
